import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, AuditLogEntry } from '../../api/admin';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';

const PAGE_SIZE = 50;

export default function AdminAuditLog() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [detailEntry, setDetailEntry] = useState<AuditLogEntry | null>(null);

  const { data: entries = [], isLoading, isFetching } = useQuery({
    queryKey: ['admin-audit-log', offset],
    queryFn: () => adminApi.getAuditLog(PAGE_SIZE, offset).then((r) => r.data),
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-primary-600 hover:text-primary-700 text-sm">{t('admin.backToAdmin')}</Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{t('admin.auditLogTitle')}</h1>
      </div>

      {/* Detail modal */}
      {detailEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setDetailEntry(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {detailEntry.action} — {detailEntry.entity_type}
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">{t('admin.detailsOldValue')}</p>
                <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                  {detailEntry.old_value ? JSON.stringify(detailEntry.old_value, null, 2) : '–'}
                </pre>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">{t('admin.detailsNewValue')}</p>
                <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                  {detailEntry.new_value ? JSON.stringify(detailEntry.new_value, null, 2) : '–'}
                </pre>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setDetailEntry(null)} className="btn btn-secondary">{t('admin.close')}</button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse h-14" />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">{t('admin.colDate')}</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">{t('admin.colAction')}</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">{t('admin.colEntity')}</th>
                  <th className="text-left py-2 pr-4 font-medium text-gray-500">{t('admin.colPerformedBy')}</th>
                  <th className="py-2 text-gray-500 font-medium">{t('admin.colDetails')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString(getDateLocale())}
                    </td>
                    <td className="py-2 pr-4 font-medium text-gray-900">{entry.action}</td>
                    <td className="py-2 pr-4 text-gray-700">
                      <span className="text-gray-400">{entry.entity_type}</span>
                      {entry.entity_name && <span className="ml-1.5">{entry.entity_name}</span>}
                    </td>
                    <td className="py-2 pr-4 text-gray-700">
                      {entry.performed_by_name ?? (entry.performed_by
                        ? <span className="text-gray-400 font-mono text-xs">{entry.performed_by}</span>
                        : '–')}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => setDetailEntry(entry)}
                        className="text-primary-600 hover:text-primary-700 text-xs font-medium"
                      >
                        {t('admin.colDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && (
              <p className="text-center text-gray-500 py-8">{t('admin.noAuditEntries')}</p>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <button
          onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          disabled={offset === 0}
          className="btn btn-secondary disabled:opacity-50"
        >
          ←
        </button>
        <button
          onClick={() => setOffset((o) => o + PAGE_SIZE)}
          disabled={isFetching || entries.length < PAGE_SIZE}
          className="btn btn-secondary disabled:opacity-50"
        >
          {t('admin.loadMore')} →
        </button>
      </div>
    </div>
  );
}
